export function formatReplacementDemandNumber(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value || 0);
}

export function formatReplacementDemandUnits(value) {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

export function formatReplacementDemandPercent(value) {
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format((value || 0) * 100)}%`;
}

export function formatReplacementDemandDate(value) {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function isReplacementDemandShiftWindowActive(startDate, endDate, today = new Date()) {
  if (!startDate || !endDate) return false;
  const start = new Date(`${startDate}T00:00:00Z`).getTime();
  const end = new Date(`${endDate}T00:00:00Z`).getTime();
  const todayMs = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return start <= todayMs && todayMs <= end;
}

export function rowHasReplacementDemandException(row) {
  return (
    row.missingOriginal ||
    row.missingReplacement ||
    row.shiftPercent <= 0 ||
    (row.shiftedUnits != null &&
      row.originalForecastUnits != null &&
      row.shiftedUnits > row.originalForecastUnits + 0.001)
  );
}

export function filterReplacementDemandRows(
  rows,
  { search = '', activeOnly = false, exceptionsOnly = false } = {}
) {
  const normalizedSearch = search.trim().toLowerCase();
  return (rows || []).filter(row => {
    const matchesSearch =
      !normalizedSearch ||
      row.originalSku.toLowerCase().includes(normalizedSearch) ||
      row.replacementSku.toLowerCase().includes(normalizedSearch) ||
      row.originalProductName.toLowerCase().includes(normalizedSearch) ||
      row.replacementProductName.toLowerCase().includes(normalizedSearch);

    const active = isReplacementDemandShiftWindowActive(row.shiftStartDate, row.shiftEndDate);
    const hasException = rowHasReplacementDemandException(row);
    return (
      matchesSearch &&
      (!activeOnly || active) &&
      (!exceptionsOnly || hasException)
    );
  });
}

export function summarizeReplacementDemandRows(filteredRows) {
  const activeCount = filteredRows.filter(row =>
    isReplacementDemandShiftWindowActive(row.shiftStartDate, row.shiftEndDate)
  ).length;
  const shiftedUnits = filteredRows.reduce((sum, row) => sum + Number(row.shiftedUnits ?? 0), 0);
  const replacementIncomingBySku = new Map();
  for (const row of filteredRows) {
    const key = String(row.replacementSku ?? '').trim();
    if (!key || key === '—' || key === '-') continue;
    if (!replacementIncomingBySku.has(key)) {
      replacementIncomingBySku.set(key, Number(row.replacementIncomingUnits ?? 0));
    }
  }
  const replacementIncomingUnits = [...replacementIncomingBySku.values()].reduce(
    (sum, value) => sum + value,
    0
  );
  const originalUnits = filteredRows.reduce(
    (sum, row) => sum + Number(row.originalForecastUnits ?? 0),
    0
  );
  const exceptionCount = filteredRows.filter(row => rowHasReplacementDemandException(row)).length;

  return {
    ruleCount: filteredRows.length,
    activeCount,
    shiftedUnits,
    replacementIncomingUnits,
    originalUnits,
    exceptionCount,
  };
}

export function buildReplacementDemandRecipientGroups(filteredRows, limit = 8) {
  const grouped = new Map();
  filteredRows.forEach(row => {
    const current = grouped.get(row.replacementSku);
    if (current) {
      current.sourceCount += 1;
      return;
    }
    grouped.set(row.replacementSku, {
      replacementSku: row.replacementSku,
      replacementProductName: row.replacementProductName,
      incomingUnits: Number(row.replacementIncomingUnits ?? 0),
      sourceCount: 1,
    });
  });
  return Array.from(grouped.values())
    .sort(
      (left, right) =>
        right.incomingUnits - left.incomingUnits ||
        left.replacementSku.localeCompare(right.replacementSku)
    )
    .slice(0, limit);
}
